<template>
  <div class="wb-landing">
    <section class="wb-hero">
      <div class="hero-inner">
        <h1 class="hero-title">Transport Data, Modeling & Analytics (TDMA)</h1>
        <div class="hero-subtitle container text-left">
          <p>
            A comprehensive Global Knowledge Platform supporting data-driven
            transport planning, efficient infrastructure investment, and
            climate-resilient solutions. It is designed to support the global
            transport community in enhancing data-driven transport planning and
            investments, optimizing transport infrastructure and logistics, and
            promoting clean and resilient transport solutions. By leveraging
            advanced tools, data, and models across three strategic pillars, the
            platform aims to address data gaps and provide comprehensive support
            to both internal and external clients in improving transport
            planning, achieving cleaner transport, and developing resilient and
            sustainable transport solutions.
          </p>

          <p>
            The Global Knowledge Platform, aligned with the World Bank's
            Knowledge Compact for Action, will play an important role in
            data-informed decision-making, improving the inclusion and
            accessibility of transport systems, optimizing resource allocation,
            making efficient infrastructure investments, increasing the
            availability and accessibility of transport data and models, and
            achieving long-term economic and environmental goals in the
            transport sector.
          </p>
        </div>
      </div>
    </section>
    

    <section class="wb-stats">
      <div class="stat">
        <div class="num">3</div>
        <div class="lbl">Strategic Pillars</div>
      </div>
      <div class="stat">
        <div class="num">10+</div>
        <div class="lbl">Datasets, Tools & Models</div>
      </div>
      <div class="stat">
        <div class="num">Global</div>
        <div class="lbl">Coverage</div>
      </div>
    </section>

    <section class="strategic-pillars">
      <div class="pillars-container">
        <h2 class="pillars-title">Three Strategic Pillars</h2>
        <div class="pillars-grid">
          <div class="pillar-card">
            <div class="pillar-number">01</div>
            <h3 class="pillar-title">Economic Modeling for Transport Infrastructure</h3>
            <p class="pillar-description">
              Develop robust frameworks for evaluating economic viability of transport projects,
              optimizing resource allocation and guiding policy decisions at project, network, urban,
              and country levels.
            </p>
            <div class="pillar-tools">
              <span class="tool-tag">HDM-5</span>
              <span class="tool-tag">PATH Model</span>
              <span class="tool-tag">FlowMax Model</span>
            </div>
          </div>

          <div class="pillar-card">
            <div class="pillar-number">02</div>
            <h3 class="pillar-title">Transport Impact Measurement</h3>
            <p class="pillar-description">
              Assess impacts of transport interventions on jobs, accessibility, inclusion, resilience, and
              environment. Includes decision-making tools for mode and technology choices.
            </p>
            <div class="pillar-tools">
              <span class="tool-tag">EVMax Tool</span>
              <span class="tool-tag">CycleMax Tool</span>
              <span class="tool-tag">Transport GHG MRV Tool</span>
              <span class="tool-tag">Resilience Tools</span>
            </div>
          </div>

          <div class="pillar-card">
            <div class="pillar-number">03</div>
            <h3 class="pillar-title">Transport Data</h3>
            <p class="pillar-description">
              Strengthen collection, management, and dissemination of transport data to support
              evidence-based investments and policies. Develop performance indices and improve
              data accessibility.
            </p>
            <div class="pillar-tools">
              <span class="tool-tag">CPPI</span>
              <span class="tool-tag">RAI</span>
              <span class="tool-tag">Transport Data Portal</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="cta-container">
        <h2 class="cta-title">Ready to Transform Transport Planning & Investment?</h2>
        <p class="cta-subtitle">
          Join the global transport community in leveraging data-driven and analytical solutions for sustainable and resilient mobility
        </p>
        <div class="cta-buttons">
          <button class="cta-btn primary">
            Explore the TDMA Global Knowledge Platform in Data360 Portal
          </button>
          <div class="cta-primary-buttons">
            <button class="cta-btn primary">TDMA Slide Deck</button>
            <button class="cta-btn primary">Contact Us</button>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default { name: "Home" };
</script>

<style scoped>
.wb-landing {
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}
.wb-hero {
  background: linear-gradient(135deg, #002244 0%, #002244 100%);
  color: #fff;
  text-align: center;
  padding: 30px 16px 10px;
}
.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 12px;
}
.hero-subtitle {
  opacity: 0.9;
  font-size: 1.05rem;
}
.cta-row {
  margin-top: 24px;
}
.wb-stats {
  display: flex;
  justify-content: center;
  gap: 10%;
  padding: 6px 26px;
  background: linear-gradient(135deg, #002244 0%, #002244 100%);
  color: #fff;
}

@media (max-width: 640px) {
  .wb-stats {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
}
.stat {
  text-align: center;
}
.num {
  font-size: 2rem;
  font-weight: 800;
}
.lbl {
  opacity: 0.85;
}

.strategic-pillars {
  padding: 30px 10px;
  background: #f8f9fa;
}

.pillars-container {
  max-width: 1500px;
  margin: 0 auto;
}

.pillars-title {
  text-align: center;
  font-size: 2rem;
  color: #002244;
  margin-bottom: 10px;
}

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 50px;
  justify-items: center;
}

.pillar-card {
  background: #002244;
  color: white;
  padding: 10px 20px;
  border-radius: 12px;
  max-width: 500px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 34, 68, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.pillar-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 32px rgba(0, 34, 68, 0.25);
}

.pillar-number {
  font-size: 2rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 10px;
}

.pillar-title {
  font-weight: 700;
  margin-bottom: 10px;
  line-height: 1.3;
}

.pillar-description {
  flex-grow: 1;
  margin-bottom: 30px;
  /* line-height: 1.6; */
  opacity: 0.9;
}

.pillar-tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tool-tag {
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.cta-section {
  background: #002244;
  color: white;
  padding: 40px 20px;
  text-align: center;
}

.cta-container {
  max-width: 1000px;
  margin: 0 auto;
}

.cta-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 10px;
  line-height: 1.2;
}

.cta-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 20px;
  line-height: 1.4;
}

.cta-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.cta-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 20px;
  font-weight: 300;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.cta-btn.primary {
  background: #009FDA;
  color: white;
  padding: 12px 20px;
}

.cta-btn.primary:hover {
  background: #009FDA;
  transform: translateY(-2px);
}

.cta-primary-buttons {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
}

/* .cta-btn.secondary {
  background-color: #009FDA;
  color: white;
  border: 2px solid #00aaff;
  min-width: 150px;
} */

.cta-btn.secondary:hover {
  background: #00aaff;
  color: white;
}

@media (max-width: 768px) {
  .cta-title {
    font-size: 1.6rem;
  }
  
  .cta-subtitle {
    font-size: 1rem;
  }
  
  .cta-btn.primary {
    min-width: auto;
    width: 90%;
    max-width: 350px;
  }
  
  .cta-secondary-buttons {
    flex-direction: column;
    width: 100%;
  }
  
  .cta-btn.secondary {
    min-width: auto;
    width: 80%;
    max-width: 200px;
  }
  
  .cta-section {
    padding: 30px 15px;
  }
}

@media (max-width: 640px) {
  .cta-title {
    font-size: 1.4rem;
    margin-bottom: 12px;
  }
  
  .cta-subtitle {
    margin-bottom: 25px;
  }
}

@media (max-width: 768px) {
  .pillars-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .pillar-card {
    max-width: none;
    margin: 0 10px;
  }
  
  .pillars-title {
    font-size: 1.6rem;
    margin-bottom: 30px;
  }
  
  .strategic-pillars {
    padding: 30px 20px;
  }
}

@media (max-width: 640px) {
  .wb-stats {
    gap: 24px;
  }
  .hero-title {
    font-size: 2.2rem;
  }
}
</style>
